import { getInitialAppInstallationDataUrl, getPathToMessages } from '../internal-use';
import { APP_CSS_FILE, APP_STATIC_BUNDLE_FILE, getFailedScriptError, INSTALLER_TOPOLOGY_API_URL, LOAD_SCRIPT_TRANSACTION } from '../utils';
import { createPanoramaClient } from '../panorama';
import { HttpClient } from '@wix/http-client';
const httpClient = new HttpClient();
let getInstallAppForConsumer;
let installAppApi;
let panoramaClientPromise;
let panoramaClient;
let preInstallationDataCache;
let scriptLoaded = false;
let cssLoaded = false;
let injectScriptPromise;
let messagesPromise;
let promiseStatus;
let scriptLoadEventCallback;
let cssLinkLoadEventCallback;
let installerTopology;
let rejectScript;
let scriptElement = null;
let cssLinkElement = null;
let isInstallerExecutionRunning = false;
export const invalidateCache = () => {
  preInstallationDataCache = undefined;
};
const resetVars = () => {
  scriptLoaded = false;
  cssLoaded = false;
  scriptLoadEventCallback = undefined;
  cssLinkLoadEventCallback = undefined;
  scriptElement = null;
  cssLinkElement = null;
  promiseStatus = undefined;
  installAppApi = undefined;
  rejectScript = undefined;
  installerTopology = undefined;
  isInstallerExecutionRunning = false;
  messagesPromise = undefined;
  preInstallationDataCache = undefined;
};
export const unmountCleanup = () => {
  // unmount cleanup only if a promise was defined - otherwise promise might still not fulfilled
  if (promiseStatus) {
    if (scriptElement) {
      scriptLoadEventCallback && scriptElement.removeEventListener('load', scriptLoadEventCallback);
      rejectScript && scriptElement.removeEventListener('error', rejectScript);
      document.head.removeChild(scriptElement);
      scriptElement = null;
    }
    if (cssLinkElement) {
      cssLinkLoadEventCallback && cssLinkElement.removeEventListener('load', cssLinkLoadEventCallback);
      rejectScript && cssLinkElement.removeEventListener('error', rejectScript);
      // do not remove cssLink from document.head, because on navigation the stylesheet will be lost
      cssLinkElement = null;
    }
  }

  // should reset vars only if the promise was rejected, so it will fetch api again cleanly
  // otherwise, keep the global vars for unmount-remount situation
  promiseStatus === 'rejected' && resetVars();
};
const scriptLoadEvent = resolveScript => {
  // loading script tags in SSR projects is done only on client-side, so window should be well-defined here
  if (window.__INSTALL_APP__) {
    installAppApi = window.__INSTALL_APP__;
    delete window.__INSTALL_APP__;
    scriptLoaded = true;
    if (cssLoaded) {
      resolveScript();
    }
  } else {
    rejectScript('no window.__INSTALL_APP__ found when script loaded');
  }
};
const cssLinkLoadEvent = resolveScript => {
  cssLoaded = true;
  if (scriptLoaded) {
    resolveScript();
  }
};
const injectScript = async (_resolve, _reject) => {
  // a global var for the unmount cleanup
  rejectScript = e => {
    promiseStatus = 'rejected';
    const scriptError = getFailedScriptError(panoramaClient, e);
    _reject(scriptError);
  };
  panoramaClient = await panoramaClientPromise; // await for the panorama client to be ready (it's probably resolved by now - initiated at the beginning of the hook)
  panoramaClient.transaction(LOAD_SCRIPT_TRANSACTION).start();
  httpClient.get(INSTALLER_TOPOLOGY_API_URL).then(_ref => {
    let {
      data: topologyData
    } = _ref;
    const {
      languageCode,
      topology: {
        appInstallerClientApi,
        translations
      }
    } = topologyData;
    if (!appInstallerClientApi) {
      rejectScript('appInstallerClientApi topology is missing');
    }
    const resolveScript = () => {
      promiseStatus = 'resolved';
      _resolve();
    };
    installerTopology = topologyData;
    const cssHref = appInstallerClientApi + APP_CSS_FILE;
    const scriptSrc = appInstallerClientApi + APP_STATIC_BUNDLE_FILE;
    const isCssLinkPreloaded = Boolean(document.head.querySelector(`link[href="${cssHref}"]`));
    const preloadedScriptElement = document.head.querySelector(`script[src="${scriptSrc}"]`);
    if (isCssLinkPreloaded) {
      // preloaded css can be reused
      cssLoaded = true;
    } else {
      cssLinkElement = document.createElement('link');
      cssLinkElement.rel = 'stylesheet';
      cssLinkElement.href = cssHref;
      cssLinkLoadEventCallback = () => cssLinkLoadEvent(resolveScript);
      cssLinkElement.addEventListener('load', cssLinkLoadEventCallback);
      cssLinkElement.addEventListener('error', rejectScript);
      document.head.appendChild(cssLinkElement);
    }
    scriptElement = document.createElement('script');
    scriptElement.src = scriptSrc;
    scriptLoadEventCallback = () => scriptLoadEvent(resolveScript);
    scriptElement.addEventListener('load', scriptLoadEventCallback);
    scriptElement.addEventListener('error', rejectScript);
    if (preloadedScriptElement) {
      // cannot be reused, so cleanup
      document.head.removeChild(preloadedScriptElement);
    }
    document.head.appendChild(scriptElement);
    if (!messagesPromise) {
      messagesPromise = httpClient.get(getPathToMessages(translations, languageCode)).then(_ref2 => {
        let {
          data: messages
        } = _ref2;
        return messages;
      }); // errors will be handled in the client;
    }
  }).catch(rejectScript);
};

// use only when installAppApi is defined
const installAppExecution = _ref3 => {
  let {
    installAppProps,
    implementerProps: {
      platform,
      ...implementerProps
    }
  } = _ref3;
  isInstallerExecutionRunning = true;
  panoramaClient.reportLoadStart({
    appId: installAppProps.appId,
    tenantId: installAppProps.tenantId,
    url: window.location.href
  });
  return installAppApi({
    ...installAppProps,
    ...implementerProps,
    messagesPromise,
    essentials: {
      httpClient,
      platform
    },
    panoramaClient: panoramaClient,
    topology: installerTopology,
    preInstallationDataCache,
    invalidateCache
  }).finally(() => {
    isInstallerExecutionRunning = false;
  });
};
export const implementInstallerInHost = props => {
  if (getInstallAppForConsumer) {
    // if multiple callers retrieve the script within the same artifact, each caller will get its own implementation with the props.
    return getInstallAppForConsumer(props);
  }
  if (!panoramaClientPromise) {
    panoramaClientPromise = createPanoramaClient(props.platform);
  }

  // it's a function in order to get the runtime snapshot of promiseStatus inside installApp function
  const wasPromiseRejected = () => promiseStatus === 'rejected';

  // if there is a promise, and it was not rejected, return the installApp that was created already
  // otherwise, let's run it (could be repeated)
  if (!injectScriptPromise || wasPromiseRejected()) {
    const runScriptPromise = () => {
      wasPromiseRejected() && unmountCleanup();
      injectScriptPromise = new Promise((_resolve, _reject) => injectScript(_resolve, _reject)).finally(() => {
        var _panoramaClient;
        (_panoramaClient = panoramaClient) == null || _panoramaClient.transaction(LOAD_SCRIPT_TRANSACTION).finish();
      });
    };

    // initial run
    runScriptPromise();
    getInstallAppForConsumer = implementerProps => async installAppProps => {
      if (typeof window === 'undefined') {
        throw new Error('App Installer should not be invoked on SSR');
      }
      if (isInstallerExecutionRunning) {
        throw new Error('Installer is already running');
      }
      const executionArgs = {
        installAppProps,
        implementerProps
      };
      if (installAppApi && promiseStatus === 'resolved') {
        return installAppExecution(executionArgs);
      } else {
        wasPromiseRejected() && runScriptPromise(); // in case initial run failed, on click will trigger it again
        await injectScriptPromise; // if promise was not resolved yet, wait for it

        // installAppApi could be undefined by type definition
        if (installAppApi) {
          return installAppExecution(executionArgs);
        } else {
          throw getFailedScriptError(panoramaClient, 'Failed to return installAppApi after a successful script load');
        }
      }
    };
  }
  return getInstallAppForConsumer(props);
};
export const prefetchData = _ref4 => {
  let {
    appId,
    tenantId,
    version,
    ...rest
  } = _ref4;
  if (!appId || !tenantId) {
    return;
  }
  const {
    appId: cachedAppId,
    tenantId: cachedTenantId
  } = preInstallationDataCache || {};
  if (appId !== cachedAppId || tenantId !== cachedTenantId) {
    preInstallationDataCache = {
      appId,
      tenantId,
      version,
      preFetchPromise: httpClient.get(getInitialAppInstallationDataUrl({
        appId,
        version,
        tenantId,
        ...rest
      }))
    };
  }
};
export const prefetchDataInternal = params => {
  prefetchData(params);
};
//# sourceMappingURL=helperFunctions.js.map